import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Info, Loader } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { UPDATE_COMPANY_MUTATION } from '@/components/layout/breadcrumbs/company-switcher/gql'
import { USER_COMPANIES_QUERY } from '@/components/root/gql'
import { ManageBillingButton } from '@/components/shared/manage-billing-button'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
} from '@/components/layout/breadcrumbs/company-switcher/gql.types.gen'

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'Company name is required' })
    .regex(/^[\p{L}\p{N}\p{P}\p{Z}]+$/u, {
      message: 'Company name contains non-alphanumeric unicode characters',
    }),
})

type Props = {
  isOpen: boolean
  onClose: () => void
  idCompany: string
  initialName: string
}

export const UpdateCompanySheet = ({
  isOpen,
  onClose,
  idCompany,
  initialName,
}: Props) => {
  const [isPending, startTransition] = useTransition()

  const [updateCompany] = useMutation<
    UpdateCompanyMutation,
    UpdateCompanyMutationVariables
  >(UPDATE_COMPANY_MUTATION)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: initialName },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = form

  useEffect(() => {
    if (isOpen) {
      reset({ name: initialName })
    }
  }, [isOpen, initialName, reset])

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        await updateCompany({
          variables: { id: idCompany, name: data.name },
          refetchQueries: [{ query: USER_COMPANIES_QUERY }],
          awaitRefetchQueries: true,
        })
        toast(
          <ToasterContent variant="success">
            Company updated successfully
          </ToasterContent>
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to update company
          </ToasterContent>
        )
      }
    })
  }

  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Update company name</SheetTitle>
        </SheetHeader>
        <div className="px-4 overflow-y-auto w-full pb-2 space-y-6">
          <Alert>
            <Info />
            <AlertTitle>Only shown on your dashboard</AlertTitle>
            <AlertDescription>
              <p>
                This name is only visible inside Sevalla. It is not used on
                invoices or other billing documents. To update billing details,
                open your billing settings.
              </p>
            </AlertDescription>
            <ManageBillingButton
              variant="outline"
              label="Update billing settings"
              className="col-start-2 mt-2 justify-self-start"
            />
          </Alert>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        aria-label="Company name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" hidden />
            </form>
          </Form>
        </div>
        <SheetFooter className="sticky">
          <Button
            disabled={isPending || !isValid || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Update company name
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
